import ServicePageLayout from './ServicePageLayout';
import { Building2, Users, FileText, Scale, Shield, TrendingUp, Heart, Globe, BookOpen, MessageSquare } from 'lucide-react';

const relatedServices = [
  { path: '/conseil-rh/', label: 'Conseil RH Particuliers' },
  { path: '/droit-social/', label: 'Expertise Droit Social' },
  { path: '/formation-rh/', label: 'Formations RH' },
  { path: '/audit-rh/', label: 'Audit RH' },
];

const services = [
  {
    icon: Building2,
    title: "Accompagnement a la transformation RH",
    description: "Les collectivites territoriales font face a des evolutions majeures : reforme de la fonction publique territoriale, digitalisation, nouvelles attentes des agents. J accompagne les directions des ressources humaines dans la conduite du changement et la modernisation de leurs pratiques RH avec une approche progressive et concertee.",
  },
  {
    icon: Users,
    title: "Gestion des carrieres et des mobilites",
    description: "Conception et mise en oeuvre de politiques de gestion previsionnelle des emplois et des competences (GPEC). J aide les collectivites a anticiper les departs en retraite, a preparer les reclassements et a optimiser les parcours professionnels des agents territoriaux dans le respect du statut.",
  },
  {
    icon: FileText,
    title: "Redaction de referentiels et classements",
    description: "Expertise dans la redaction et la revision des referentiels de competences, des classifications de postes et des grilles indiciaires. Je m assure de la coherence avec les dispositions statutaires et de la perennite juridique de vos documents RH.",
  },
  {
    icon: Scale,
    title: "Conformite statutaire et juridique",
    description: "Veille sur la conformite de vos pratiques RH avec le statut de la fonction publique territoriale. Audit des actes de gestion, des procedures de recrutement, des promotions et des mutations. Prevention des contentieux devant les juridictions administratives.",
  },
  {
    icon: Shield,
    title: "Prevention des risques professionnels",
    description: "Accompagnement dans la mise en place du document unique d evaluation des risques professionnels (DUER), la gestion des accidents de service et des maladies professionnelles. Formation des referents SST et des membres du CHSCT/CSST.",
  },
  {
    icon: TrendingUp,
    title: "Accompagnement au dialogue social",
    description: "Preparation et animation des instances de dialogue social (CAP, CTP, CT, CHSCT/CSST). Je forme les elus et les representants du personnel a leurs missions, redige les rapports annuels et accompagne la negociation collective dans le respect du cadre statutaire.",
  },
  {
    icon: Heart,
    title: "Qualite de vie au travail des agents",
    description: "Diagnostic et mise en place de programmes QVT adaptes au contexte territorial. Gestion des situations individuelles sensibles, prevention des risques psychosociaux, et accompagnement au retour de longue maladie ou conges.",
  },
  {
    icon: Globe,
    title: "Politique d egalite et diversite",
    description: "Elaboration et suivi du plan d action pour l egalite professionnelle entre les femmes et les hommes. Diagnostic des ecarts de remuneration, mise en place d actions correctives, et accompagnement dans l obtention du label diversite.",
  },
  {
    icon: BookOpen,
    title: "Formation des elus et cadres territoriaux",
    description: "Formations dediees aux specificites de la fonction publique territoriale : statut des agents, gestion des carrieres, droit disciplinaire, responsabilite des elus. Des sessions adaptees aux contraintes des elus et des cadres territoriaux.",
  },
  {
    icon: MessageSquare,
    title: "Communication interne et engagement",
    description: "Conception de strategies de communication interne pour ameliorer l engagement des agents. Accompagnement dans la mise en place d enquetes de satisfaction, de barometres internes et de plans d action pour renforcer le sentiment d appartenance.",
  },
];

export default function AccompagnementCollectivitesPage() {
  return (
    <ServicePageLayout
      title="Accompagnement RH des Collectivites Territoriales | Aurore RH"
      description="Expertise RH pour collectivites territoriales : gestion des carrieres, conformite statutaire, dialogue social et prevention des risques."
      canonical="https://aurorerh.fr/accompagnement-collectivites/"
      relatedServices={relatedServices}
    >
      <div className="prose dark:prose-invert max-w-none mb-16">
        <p className="text-lg text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed mb-6">
          Les collectivites territoriales occupent une place unique dans le paysage RH francais. Entre statut de la 
          fonction publique territoriale, specificites locales et evolutions legislatives constantes, la gestion des 
          ressources humaines territoriales requiert une expertise pointue et une comprehension fine des enjeux 
          politiques et administratifs.
        </p>
        <p className="text-lg text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed mb-6">
          En tant que consultante RH avec une maitrise en droit public, je combine une connaissance approfondie du 
          cadre juridique des collectivites et une experience terrain significative. J interviens aupres des 
          communes, intercommunalites, departements et regions pour les accompagner dans leurs defis RH quotidiens 
          et strategiques.
        </p>
      </div>

      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-10">
        Nos prestations pour les collectivites territoriales
      </h2>

      <div className="space-y-8 mb-16">
        {services.map((service, index) => (
          <div
            key={index}
            className="flex gap-5 p-6 bg-white dark:bg-warm-gray-800 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-aurore-light/20 dark:border-warm-gray-700"
          >
            <div className="flex-shrink-0 w-14 h-14 bg-aurore-light/40 dark:bg-warm-gray-700 rounded-xl flex items-center justify-center">
              <service.icon className="w-7 h-7 text-aurore-dark dark:text-aurore-light" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-warm-gray-900 dark:text-warm-gray-100 mb-2">
                {service.title}
              </h3>
              <p className="text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed">
                {service.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-6">
        Pourquoi choisir un accompagnement externe ?
      </h2>
      <div className="grid sm:grid-cols-2 gap-6 mb-16">
        {[
          {
            title: "Independance et objectivite",
            desc: "Un regard exterieur sans enjeu politique ni hierarchique pour des diagnostics sinceres et des recommandations pertinentes.",
          },
          {
            title: "Expertise statutaire pointue",
            desc: "Maitrise approfondie du statut de la fonction publique territoriale et de ses evolutions recentes.",
          },
          {
            title: "Flexibilite budgetaire",
            desc: "Intervention ponctuelle ou reguliere selon vos besoins et votre budget, sans cout fixe de personnel.",
          },
          {
            title: "Confidentialite garantie",
            desc: "Traitement discret des situations sensibles avec une charte deontologique stricte.",
          },
        ].map((item, i) => (
          <div key={i} className="p-5 bg-warm-gray-50 dark:bg-warm-gray-800/50 rounded-xl">
            <h3 className="font-semibold text-warm-gray-900 dark:text-warm-gray-100 mb-2">{item.title}</h3>
            <p className="text-warm-gray-700 dark:text-warm-gray-300 text-sm">{item.desc}</p>
          </div>
        ))}
      </div>

      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-6">
        Types de collectivites accompagnees
      </h2>
      <div className="flex flex-wrap gap-3 mb-16">
        {["Communes", "Intercommunalites", "Departements", "Regions", "EPCI", "Syndicats mixtes", "Etablissements publics territoriaux"].map((tag) => (
          <span key={tag} className="px-4 py-2 bg-aurore-light/30 dark:bg-warm-gray-700 text-aurore-dark dark:text-aurore-light rounded-full text-sm font-medium">
            {tag}
          </span>
        ))}
      </div>

      <div className="bg-gradient-to-br from-aurore-pale to-aurore-light/30 dark:from-warm-gray-800 dark:to-warm-gray-800 rounded-2xl p-8">
        <h3 className="text-xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-4">
          Conformite RGPD et protection des donnees
        </h3>
        <p className="text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed">
          L accompagnement des collectivites integre systematiquement les enjeux de protection des donnees personnelles. 
          Je veille a ce que toutes les pratiques RH recommandees soient conformes au RGPD et aux specificites de la 
          fonction publique territoriale en matiere de gestion des donnees des agents.
        </p>
      </div>
    </ServicePageLayout>
  );
}
